import { createLabManifest } from "@/helpers/lab_utils/labManifest";
import {
  createLabPassage,
  completionPassageExtras
} from "@/helpers/lab_utils/labPassage";

// Linear flow through instrument education, then experiment steps. Lab Area unlocks at "the-instrument".

const passages = [
  createLabPassage({
    id: "start",
    progressLabel: "Welcome",
    title: "Analytical Instrumentation in ChE, BioE and EnvE",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Welcome to CHE 417/517: Analytical Instrumentation Laboratory!"
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Laboratory 2: Characterization of chemicals and polymers using FT-IR"
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "The purpose of this experiment is to demonstrate sample analysis using a Fourier transform infrared (FT-IR) spectroscopy using an attenuated total reflection (ATR) cell."
                }
              ]
            }
          ]
        }
      },
      {
        type: "custom",
        name: "skip-to-measurements",
        skipTo: "unknown-samples"
      }
    ],
    choices: [
      {
        text: "Next",
        to: "introduction"
      }
    ]
  }),

  createLabPassage({
    id: "introduction",
    progressLabel: "Introduction",
    title: "Introduction: Infrared Spectroscopy",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Infrared spectroscopy is one of the most common techniques for the analysis of materials. One of the reasons for this is that infrared spectra can be obtained from a wide range of samples (solids, liquids, gases) and under a broad range of conditions (pressure, temperature, etc.). Advances in IR spectroscopy has allowed new applications to be developed, however the identification of compounds is one of the primary uses of the technique."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Infrared spectroscopy is based on the coupling of electromagnetic radiation with fundamental vibrational frequencies in molecules. During this process, the electromagnetic radiation and the vibrational transition need to have the same frequency. Another requirement for infrared spectroscopy is that a dipole change needs to occur for the vibrational transition for the electromagnetic radiation to be absorbed. The vibrational frequencies can be estimated using Hooke's law where the frequency ("
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "\\nu" }
                },
                {
                  type: "text",
                  text: ") is given by:"
                }
              ]
            },
            {
              type: "blockMath",
              attrs: {
                latex: "\\nu = \\frac{1}{2\\pi} \\sqrt{\\frac{k}{\\mu}}"
              }
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "where:"
                },
                {
                  type: "hardBreak"
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "\\mu = \\frac{M_1 M_2}{M_1 + M_2}" }
                },
                {
                  type: "text",
                  text: " (reduced mass)"
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "where the force constant ("
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "k" }
                },
                {
                  type: "text",
                  text: ") and reduced mass ("
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "\\mu" }
                },
                {
                  type: "text",
                  text: ") can be correlated to bond strength and atomic mass ("
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "M_a" }
                },
                {
                  type: "text",
                  text: ") of the atoms in the molecule, respectively."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [
      {
        text: "Next",
        to: "ft-ir-advancement"
      }
    ]
  }),

  createLabPassage({
    id: "ft-ir-advancement",
    progressLabel: "FT-IR Theory",
    title: "Fourier Transform Infrared Spectroscopy",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "A significant advancement of the infrared spectroscopy technique was the integration of a Michelson interferometer, which allows the collection of an interferogram that is a time-domain measurement. Performing a Fourier transform on the interferogram allows the conversion of the time-domain spectrum to the frequency-domain, which is a standard infrared spectrum."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "A schematic of an FT-IR system is shown below. The main components include the light source, the Michelson interferometer, the sample compartment, the detector and associated electronics, and the computerized instrument control which includes data collection and analysis."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [
      {
        text: "Next",
        to: "the-instrument"
      }
    ]
  }),

  createLabPassage({
    id: "the-instrument",
    progressLabel: "Instrument",
    title: "The FT-IR Instrument",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "The virtual instrument in this activity is modeled after a Thermo Scientific Nicolet 6700 FT-IR spectrometer. On the exterior, the major regions are the IR source housing, the interferometer housing, the sample compartment (where the ATR accessory sits), the detector compartment, and external beam ports for optional accessories."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Open the Lab Area to explore a labeled exterior diagram. Click each region to read how it contributes to the measurement."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [
      {
        text: "Next",
        to: "internal-components"
      }
    ]
  }),

  createLabPassage({
    id: "internal-components",
    progressLabel: "Internal Components",
    title: "Internal Components",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Inside the bench, infrared radiation is collimated and sent through a Michelson interferometer: a beam splitter sends one part of the beam to a fixed mirror and another to a moving mirror. After recombination, the modulated beam travels to the sample compartment and then to the detector. The Internal Components tab shows a simplified top-down layout with the IR source, beam splitter, mirrors, ATR sample compartment, and front/back detectors."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [
      {
        text: "Next",
        to: "ir-beam-path"
      }
    ]
  }),

  createLabPassage({
    id: "ir-beam-path",
    progressLabel: "IR Beam",
    title: "The IR Beam Path",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Use the IR Beam tab to step through how radiation travels from the source through the interferometer, into the sample region, and to the detector. Follow the on-screen controls (Previous, Next, Play, Replay) to match the narration in this section."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [
      {
        text: "Next",
        to: "atr-principle"
      }
    ]
  }),

  createLabPassage({
    id: "atr-principle",
    progressLabel: "ATR",
    title: "Attenuated Total Reflection (ATR)",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "For our experiments, we will be using attenuated total reflection (ATR). The ATR crystal is made of a material with a high refractive index (diamond in our case with a fixed angle of 42°) and allows total internal reflection of the infrared radiation. At each reflection in the ATR crystal an evanescent wave penetrates outside of the high refractive index material surface. This is where we will place our sample and absorption losses in the ATR setup is due to absorption of the evanescent wave. The evanescent wave penetrates only a very short distance outside of the ATR crystal (~2 μm), which indicates that the sample must be pressed firmly against the surface of the ATR crystal."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Another powerful use of ATR is to analyze very thin films (down to monolayers) by varying the incidence angle of the incoming radiation. The evanescent wave extends a distance into the material, and is given by the following equation:"
                }
              ]
            },
            {
              type: "blockMath",
              attrs: {
                latex:
                  "d_p = \\frac{\\lambda}{2\\pi n_{ire} \\sqrt{\\sin^2 \\theta - (n_{smp} / n_{ire})^2}}"
              }
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "where:"
                },
                {
                  type: "hardBreak"
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "d_p" }
                },
                {
                  type: "text",
                  text: " = depth of penetration"
                },
                {
                  type: "hardBreak"
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "n_{ire}" }
                },
                {
                  type: "text",
                  text: " = refractive index of the analysis crystal"
                },
                {
                  type: "hardBreak"
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "n_{smp}" }
                },
                {
                  type: "text",
                  text: " = refractive index of sample"
                },
                {
                  type: "hardBreak"
                },
                {
                  type: "inlineMath",
                  attrs: { latex: "\\theta" }
                },
                {
                  type: "text",
                  text: " = radiation incidence angle"
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [
      {
        text: "Next",
        to: "experiment-overview"
      }
    ]
  }),

  createLabPassage({
    id: "experiment-overview",
    progressLabel: "Experiment Overview",
    title: "The Experiment",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "In this virtual lab you will collect FT-IR spectra for five unknown samples labeled Sample A through Sample E using an ATR accessory. When comparing spectra across techniques or accessories, remember that ATR path length is wavelength dependent; quantitative comparisons to transmission data require care."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Use the Experiment tab in the Lab Area to load each unknown sample and collect its spectrum. You may export measurements from the Attempts view after saving your attempt."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [
      {
        text: "Next",
        to: "unknown-samples"
      }
    ]
  }),

  createLabPassage({
    id: "unknown-samples",
    progressLabel: "Unknown Samples",
    title: "Step 1: Samples A through E",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Collect a spectrum for each unknown Sample A through Sample E. The samples are not identified by chemical name — use peak positions and functional-group regions to interpret your spectra and support conclusions in your report."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [
      {
        text: "Next",
        to: "save-attempt"
      }
    ]
  }),

  createLabPassage({
    id: "save-attempt",
    progressLabel: "Save Attempt",
    title: "Step 2: Save Your Attempt",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "When you are finished collecting spectra, save your attempt. You will move to the Attempts view where you can export CSV data for analysis in Excel, Python, or other tools."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "You may attempt the activity once, and you can revisit the workbook at any time."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [
      {
        text: "Save Attempt",
        to: "lab-complete"
      }
    ]
  }),

  createLabPassage({
    id: "lab-complete",
    progressLabel: "Lab Complete",
    title: "Laboratory Complete",
    content: [
      {
        type: "text",
        text: {
          type: "doc",
          content: [
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Congratulations! You have successfully completed Laboratory 2: FT-IR Spectroscopy for Polymer Characterization."
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "In this laboratory you have:"
                }
              ]
            },
            {
              type: "bulletList",
              content: [
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Learned the principles of FT-IR spectroscopy and ATR sampling"
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Collected and compared FT-IR spectra for multiple unknown samples using ATR sampling"
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Interpreted IR spectra using characteristic absorption bands and fingerprint regions"
                        }
                      ]
                    }
                  ]
                },
                {
                  type: "listItem",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Applied spectral evidence to compare unknown samples and support analytical conclusions"
                        }
                      ]
                    }
                  ]
                }
              ]
            },
            {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: "Export your spectral data from the Attempts view to complete the required analysis and prepare your abbreviated lab report."
                }
              ]
            }
          ]
        }
      }
    ],
    choices: [],
    ...completionPassageExtras("lab-complete")
  })
];

/** @type {import("@/types/labManifest.types.js").LabManifest} */
export const labManifest = createLabManifest({
  id: "ft-ir-spectroscopy-lab",
  displayName: "FT-IR Spectroscopy Instrument Demonstration",
  labAreaUnlockPassageId: "the-instrument",
  completionPassageId: "lab-complete",
  passages
});

export default {
  draft: passages
};
export { passages };
